/**
 * URL Domain Registry - Runtime registry for URL-loaded domains.
 *
 * Maintains URL-loaded domains separately from the static build-time domains.
 * Provides functions to register, unregister, and list URL-loaded domains.
 */

import type { DomainConfig } from "./domainRegistry.ts";
import type OntologySchema from "@semantical/modules/ontology-model/OntologySchema.ts";

/**
 * Extended DomainConfig for URL-loaded domains.
 */
export interface UrlDomainConfig extends DomainConfig {
    /** The source URL from which this domain was loaded */
    sourceUrl: string;
    /** Flag to identify URL-loaded domains */
    isUrlLoaded: true;
}

/** Runtime storage for URL-loaded domains */
const urlLoadedDomains: Map<string, UrlDomainConfig> = new Map();

/**
 * Register a URL-loaded domain.
 */
export function registerUrlDomain(config: UrlDomainConfig): void {
    urlLoadedDomains.set(config.id, config);
    console.debug(`urlDomainRegistry: Registered URL domain "${config.label}" (${config.id})`);
}

/**
 * Unregister a URL-loaded domain.
 */
export function unregisterUrlDomain(id: string): boolean {
    const result = urlLoadedDomains.delete(id);
    if (result) {
        console.debug(`urlDomainRegistry: Unregistered URL domain ${id}`);
    }
    return result;
}

/**
 * Get all URL-loaded domains.
 */
export function getUrlLoadedDomains(): UrlDomainConfig[] {
    return Array.from(urlLoadedDomains.values());
}

/**
 * Get a URL-loaded domain by ID.
 */
export function getUrlDomainById(id: string): UrlDomainConfig | undefined {
    return urlLoadedDomains.get(id);
}

/**
 * Check if a domain ID is from a URL-loaded domain.
 */
export function isUrlLoadedDomain(id: string): boolean {
    return urlLoadedDomains.has(id);
}

/**
 * Clear all URL-loaded domains.
 */
export function clearUrlDomains(): void {
    urlLoadedDomains.clear();
    console.debug("urlDomainRegistry: Cleared all URL domains");
}

/**
 * Create a URL-safe ID from a URL.
 */
function sanitizeIdFromUrl(url: string): string {
    try {
        const urlObj = new URL(url);
        const path = urlObj.hostname + urlObj.pathname;
        // Replace non-alphanumeric chars with dashes, collapse multiple dashes
        return path.replace(/[^a-zA-Z0-9]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "").toLowerCase();
    } catch {
        return url.replace(/[^a-zA-Z0-9]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "").toLowerCase();
    }
}

/**
 * Create a UrlDomainConfig from an OntologySchema.
 *
 * @param schema - The OntologySchema from the loaded ontology
 * @param sourceUrl - The URL from which the ontology was loaded
 * @param label - Optional explicit label (otherwise uses schema.name)
 */
export function createUrlDomainConfig(
    schema: OntologySchema,
    sourceUrl: string,
    label?: string
): UrlDomainConfig {
    // Generate a stable ID from the URL
    const id = `url:${sanitizeIdFromUrl(sourceUrl)}`;

    return {
        id,
        label: label || schema.name,
        ttl: "", // Empty - we reload from URL
        ns: schema.getNamespace(),
        sourceUrl,
        isUrlLoaded: true,
    };
}

/**
 * Get a URL-loaded domain by its source URL.
 */
export function getUrlDomainBySourceUrl(sourceUrl: string): UrlDomainConfig | undefined {
    for (const domain of urlLoadedDomains.values()) {
        if (domain.sourceUrl === sourceUrl) {
            return domain;
        }
    }
    return undefined;
}
